import { Button } from "@/components/ui/button";
import { heroContent } from "@/content/homepage";
import { useGeneralSettings } from "@/hooks/useGeneralSettings";
import { useHero } from "@/hooks/useHero";

interface HeroProps {
  showPricing: boolean;
  onCtaClick: (packageName?: string) => void;
  isScrolled?: boolean;
  ctaRef?: React.RefObject<HTMLDivElement>;
  onLogoClick?: () => void;
  title?: string;
}

export const Hero = ({ showPricing, onCtaClick, isScrolled = false, ctaRef, onLogoClick, title }: HeroProps) => {
  const { settings } = useGeneralSettings();
  const { hero } = useHero();

  const partners = hero.partners.length > 0 ? hero.partners : heroContent.socialProof.partners;
  const partnersMobile = hero.partners.length > 0 ? hero.partners : heroContent.socialProofMobile.partners;
  const illLeft = hero.illustration_left_url || heroContent.illustrations.left;
  const illRight = hero.illustration_right_url || heroContent.illustrations.right;
  const illRightMobile = hero.illustration_right_mobile_url || heroContent.illustrations.rightMobile;
  const logoMobile = hero.logo_mobile_url || heroContent.logoMobile || settings.logo_url;

  return (
    <section className="relative overflow-hidden bg-background min-h-screen pb-6 md:pb-8">
      {/* Mobile Layout */}
      <div className="md:hidden container relative mx-auto px-4 py-12">
        <div className="relative mx-auto max-w-4xl text-center z-10">
          {hero.badge_active && hero.badge_image_url && (
            <div className={`mb-6 flex justify-center transition-all duration-300 ${isScrolled ? 'opacity-0 scale-95 -translate-y-4' : 'opacity-100 scale-100 translate-y-0'}`}>
              <img
                src={hero.badge_image_url}
                alt={hero.badge_alt || ""}
                className="h-16 w-auto"
                width={120}
                height={64}
                loading="lazy"
              />
            </div>
          )}
          <div className={`mb-8 flex justify-center transition-all duration-300 ${isScrolled ? 'opacity-0 scale-95 -translate-y-4' : 'opacity-100 scale-100 translate-y-0'}`}>
            <img
              src={logoMobile}
              alt="Sangria"
              className="h-16 md:h-20 w-auto cursor-pointer"
              width={171}
              height={64}
              onClick={onLogoClick}
            />
          </div>

          <h1 className="mb-6 text-5xl font-bold tracking-tight text-foreground md:text-6xl lg:text-7xl">
            {title || hero.title}
          </h1>

          <p className="mb-10 text-xl text-muted-foreground md:text-2xl max-w-2xl mx-auto">
            {hero.subtitle}
          </p>

          <div ref={ctaRef}>
            <Button
              size="lg"
              onClick={() => onCtaClick()}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg hover:shadow-xl transition-all text-lg h-14 px-10 rounded-full"
            >
              {showPricing ? hero.cta_text : hero.cta_waitlist}
            </Button>
          </div>

          {/* Mobile illustration - below button (right illustration only) */}
          <div className="mt-12 mb-6 flex justify-center">
            <img
              src={illRightMobile}
              alt="Sangria illustration"
              className="w-64 h-auto opacity-50"
              width={256}
              height={230}
              fetchPriority="high"
            />
          </div>

          {/* Mobile social proof - below illustration */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-3">
              {hero.social_proof_text}
            </p>
            <div className="flex justify-center gap-4 items-center opacity-50 flex-wrap px-4">
              {partnersMobile.map((partner, idx) => (
                <img key={idx} src={partner.logo} alt={partner.alt} className="h-7 object-contain grayscale hover:grayscale-0 transition-all" loading="lazy" width={50} height={28} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout - Three Container Structure */}
      <div className="hidden md:flex container mx-auto px-4 min-h-screen flex-col pb-8">
        {/* First Container: Logo, Title, Subtitle */}
        <div className="mx-auto max-w-4xl text-center pt-12 lg:pt-16">
          {hero.badge_active && hero.badge_image_url && (
            <div className={`mb-6 flex justify-center transition-all duration-300 ${isScrolled ? 'opacity-0 scale-95 -translate-y-4' : 'opacity-100 scale-100 translate-y-0'}`}>
              <img
                src={hero.badge_image_url}
                alt={hero.badge_alt || ""}
                className="h-20 w-auto"
                width={150}
                height={80}
                loading="lazy"
              />
            </div>
          )}
          <div className={`mb-8 flex justify-center transition-all duration-300 ${isScrolled ? 'opacity-0 scale-95 -translate-y-4' : 'opacity-100 scale-100 translate-y-0'}`}>
            <img
              src={settings.logo_url}
              alt="Sangria"
              className="h-16 md:h-20 w-auto cursor-pointer"
              width={213}
              height={80}
              onClick={onLogoClick}
            />
          </div>

          <h1 className="mb-6 text-5xl font-bold tracking-tight text-foreground md:text-6xl lg:text-7xl">
            {title || hero.title}
          </h1>

          <p className="mb-10 text-xl text-muted-foreground md:text-2xl max-w-2xl mx-auto">
            {hero.subtitle}
          </p>
        </div>

        {/* Second Container: Button, Illustrations and Social Proof - Grows to fill space */}
        <div className="relative flex-1 flex flex-col min-h-0 mt-0">
          <div className="flex items-end justify-between flex-1">
            <img
              src={illLeft}
              alt="Sangria illustration"
              className="h-full max-h-[40vh] w-auto object-contain object-bottom opacity-50 pl-12"
              width={400}
              height={360}
              fetchPriority="high"
            />
            <div className="flex items-start self-start" ref={ctaRef}>
              <Button
                size="lg"
                onClick={() => onCtaClick()}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg hover:shadow-xl transition-all text-lg h-14 px-10 rounded-full"
              >
                {showPricing ? hero.cta_text : hero.cta_waitlist}
              </Button>
            </div>
            <img
              src={illRight}
              alt="Sangria illustration"
              className="h-full max-h-[40vh] w-auto object-contain object-bottom opacity-50 pr-12"
              width={400}
              height={360}
            />
          </div>

          {/* Social Proof - At bottom */}
          <div className="text-center pt-8 pb-4">
            <p className="text-base text-muted-foreground mb-4">
              {hero.social_proof_text}
            </p>
            <div className="flex justify-center gap-8 items-center opacity-50 flex-wrap">
              {partners.map((partner, idx) => (
                <img key={idx} src={partner.logo} alt={partner.alt} className="h-10 object-contain grayscale hover:grayscale-0 transition-all" loading="lazy" width={71} height={40} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
